/**
 * Scorecards: the points live on the ANSWER OPTIONS.
 *
 * A mystery-diner scorecard asks forty questions and wants two numbers out of
 * them — what the visit earned, and what it could have earned. Written as
 * logic rules that is eighty rules and a running total the engine has no way
 * to keep (a calc reads `values`, writes `calcs`, and never reads its own
 * result back). Written as points on the options it is no rules at all: the
 * question carries its own weight, a question added to a section later joins
 * the totals on its own, and renaming a field cannot leave a stale row behind.
 *
 * Three answers, three outcomes, and the middle one is the whole reason the
 * pair of totals exists:
 *
 *   Yes (4 points)  earns 4, offers 4   — the visit did the thing
 *   No  (no points) earns 0, offers 4   — it was asked, and it did not
 *   N/A (`exempt`)  earns nothing, offers nothing — it could not be judged
 *
 * A question nobody has answered yet is outside both totals as well, so the
 * pair reads honestly while the form is half filled in rather than opening at
 * "0 out of 400" and climbing.
 */
import type { FieldDefinition, FieldOption } from '../shared/types';

export interface ScoreTotals {
  /** points the answers won */
  earned: number;
  /** points those same questions had to give */
  available: number;
}

export const NO_SCORE: ScoreTotals = { earned: 0, available: 0 };

/** The points on an option — blank is zero, which is exactly what "No" earns. */
const pointsOf = (o: FieldOption): number => (typeof o.score === 'number' && Number.isFinite(o.score) ? o.score : 0);

/**
 * Is this question part of the scorecard at all?
 *
 * One option carrying a number is enough. The others score nothing without
 * being marked, because an author writing "Yes 4 / No —" means No earns zero,
 * not that No is unscored — the answer that sits OUTSIDE the scorecard is the
 * one they mark `exempt`, and that is a different thing entirely.
 */
export const isScored = (f: Pick<FieldDefinition, 'options'>): boolean =>
  (f.options || []).some((o) => typeof o.score === 'number' && Number.isFinite(o.score));

/** Compare as text, the way every other answer comparison in the engine does. */
const asText = (v: unknown) => (v === undefined || v === null ? '' : String(v));

/**
 * What one answer contributes, or null when the question is outside the
 * totals — unscored, unanswered, or answered with an `exempt` option.
 *
 * A question taking several answers earns the sum of what was ticked and
 * offers the sum of everything it could have been; a question taking one earns
 * that answer's points and offers the best answer's, since that is what a
 * perfect visit would have scored. Ticking an exempt option takes the question
 * out either way: an N/A among four ticks is still an N/A.
 *
 * A write-in ("Other") matches no option, so it scores nothing and offers
 * nothing — there is no points value for an answer the author never wrote.
 */
export function scoreAnswer(f: Pick<FieldDefinition, 'options'>, value: unknown): ScoreTotals | null {
  if (!isScored(f)) return null;
  const options = f.options || [];
  const multi = Array.isArray(value);
  const picked = (multi ? value : [value]).filter((v) => v !== undefined && v !== null && v !== '');
  if (!picked.length) return null;
  const chosen = options.filter((o) => picked.some((v) => asText(v) === asText(o.value)));
  if (!chosen.length) return null;                 // a write-in, or an answer no option offers
  if (chosen.some((o) => o.exempt)) return null;   // N/A takes the question out
  const open = options.filter((o) => !o.exempt);
  return {
    earned: chosen.reduce((n, o) => n + pointsOf(o), 0),
    available: multi
      ? open.reduce((n, o) => n + pointsOf(o), 0)
      : open.reduce((best, o) => Math.max(best, pointsOf(o)), 0),
  };
}

/** Two totals added together. */
export const addTotals = (a: ScoreTotals, b: ScoreTotals): ScoreTotals =>
  ({ earned: a.earned + b.earned, available: a.available + b.available });

/**
 * The percentage, or '' while nothing is available yet.
 *
 * Zero out of zero is not zero percent — it is a scorecard nobody has started,
 * or a section every question of which was N/A. An empty box says that; a
 * hard 0 would read as a visit that failed everything.
 */
export const scorePercent = (t: ScoreTotals): number | '' =>
  (t.available > 0 ? Math.round((t.earned / t.available) * 100) : '');
